import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from "react";
import { useCookie } from "./CookieProvider";

interface CookieConsentContextType {
  hasConsent: boolean;
  openConsentDialog: () => void;
  dialogOpen: boolean;
  handleAccept: () => void;
  handleDecline: () => void;
  handleClose: () => void;
}

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

export const useCookieConsentContext = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsentContext must be used within a CookieConsentProvider"
    );
  }
  return context;
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
  children,
}) => {
  const { getCookie, setCookie } = useCookie();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [hasConsent, setHasConsent] = useState<boolean>(false);

  useEffect(() => {
    const consent = getCookie("google_analytics");
    if (consent === "accepted") {
      setHasConsent(true);
    } else if (consent === "declined") {
      setHasConsent(false);
    } else if (!consent) {
      const isCookiePolicyPage =
        window.location.pathname.toLowerCase() === "/cookiepolicy";
      if (!isCookiePolicyPage) {
        setDialogOpen(true);
      }
    }
  }, [getCookie]);

  const handleAccept = useCallback(() => {
    setCookie("google_analytics", "accepted", 365, "/");
    setHasConsent(true);
    setDialogOpen(false);
  }, [setCookie]);

  const handleDecline = useCallback(() => {
    setCookie("google_analytics", "declined", 365, "/");
    setHasConsent(false);
    setDialogOpen(false);
  }, [setCookie]);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const openConsentDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const contextValue: CookieConsentContextType = useMemo(() => ({
    hasConsent,
    openConsentDialog,
    dialogOpen,
    handleAccept,
    handleDecline,
    handleClose,
  }), [
    hasConsent,
    openConsentDialog,
    dialogOpen,
    handleAccept,
    handleDecline,
    handleClose,
  ]);

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  return useCookieConsentContext();
};
